import { ReactNode, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import type { AppRole } from '@/types/database';

interface ProtectedRouteProps {
  requiredRole?: AppRole;
  children: ReactNode;
}

export function ProtectedRoute({ requiredRole, children }: ProtectedRouteProps) {
  const { initialLoading, user, hasRole, isLoading, role } = useAuthContext();
  const hasShownToast = useRef(false);

  // Don't deny until the role has actually loaded (prevents false
  // "no permission" flash while role data is still being fetched)
  const roleReady = !requiredRole || !!role;
  const shouldDeny = !isLoading && !initialLoading && user && requiredRole && roleReady && !hasRole(requiredRole);

  useEffect(() => {
    if (shouldDeny && !hasShownToast.current) {
      hasShownToast.current = true;
      toast.error("You don't have permission to access this page");
    }
  }, [shouldDeny]);

  if (initialLoading || (user && requiredRole && !roleReady)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate replace to="/auth" />;
  }

  if (shouldDeny) {
    return <Navigate replace to="/" />;
  }

  return <>{children}</>;
}
